import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import { usePrevious } from "../../../../helpers/hooks";
import { TextInput, FieldHeader } from "../../../../components/General";
import Container from "../../../../components/PageContainer";
import InnerContainer from "../../../../components/PageInnerContainer";
import PageTitle from "../../../../components/PageTitle";
import PageSubTitle from "../../../../components/PageSubTitle";
import Button from "../../../../components/Button";
import ProgramDefinition from "./ProgramDefinition";
import useSaveOnboarding from "../../../../hooks/mutations/admin/useSaveOnboarding";
import useGetOnboarding from "../../../../hooks/queries/admin/useGetOnboarding";
import { StepComponentProps } from "../index";

const ButtonContainer = styled("div")(`
  display: flex;
  flex-direction: row;
  gap: 10px;
`);

const SavingsProgram = ({ nextStep }: StepComponentProps) => {
  const [budget, setBudget] = useState("");
  const [currentProgramStep, setCurrentProgramStep] = useState(0);

  // store the previous state value
  // used to determine if the user has just entered the page
  const prevBudgetState = usePrevious(budget);

  const { data } = useGetOnboarding();
  const { mutate: saveOnboarding } = useSaveOnboarding({
    onSuccessHandler: () => setCurrentProgramStep(2),
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // handle the case where the user comes back to the page
    if (data) {
      const val = data.program.budget;
      if (val && !prevBudgetState) {
        setBudget(val);
        setCurrentProgramStep(1);
      }
    }
  }, [data, prevBudgetState]);

  const handleContinue = () => {
    saveOnboarding({
      programItems: { budget: budget || null },
      default_program_values: true,
    });
  };

  const programSteps = [
    {
      step: 0,
      title: "Have a matching budget for this savings program?",
      subTitle:
        "If so, we can recommend a program that fits your budget and maximizes adoption. If not, we can recommend a program designed to maximize employee adoption and outcomes.",
      renderComponent: () => (
        <div>
          <ButtonContainer>
            <Button onClick={() => setCurrentProgramStep(1)}>
              Yes, I have a budget
            </Button>
            <Button onClick={handleContinue}>
              No, recommend best practices
            </Button>
          </ButtonContainer>
          <p style={{ textAlign: "center" }}>
            The average matching budget is $190 per employee per year
          </p>
        </div>
      ),
    },
    {
      step: 1,
      title: "Define your matching budget",
      subTitle: "The average employer budget is $190 per employee per year",
      renderComponent: () => (
        <div style={{ marginTop: 24 }} key={1}>
          <FieldHeader>
            Matching Budget Per Participating Employee Per Year
          </FieldHeader>
          <TextInput
            size="small"
            placeholder="210"
            value={budget ? budget : ""}
            type="text"
            onChange={(event) => setBudget(event.target.value)}
            InputProps={{
              startAdornment: <div style={{ marginRight: 2 }}>$ </div>,
            }}
          />
          <Button onClick={handleContinue} style={{ marginTop: 32 }}>
            Continue
          </Button>
        </div>
      ),
    },
    {
      step: 2,
      title:
        "Build the savings program that works for your company and employees. ",
      subTitle:
        "💡 We’ve defaulted the most common set up for you based on the results of many other companies.",
      renderComponent: () => <ProgramDefinition nextStep={nextStep} />,
    },
  ];

  const stepData = programSteps[currentProgramStep];
  return (
    <Container>
      <PageTitle>{stepData.title}</PageTitle>
      <PageSubTitle style={{ marginBottom: currentProgramStep === 2 ? 32 : 0 }}>
        {stepData.subTitle}
      </PageSubTitle>
      <InnerContainer>{stepData.renderComponent()}</InnerContainer>
    </Container>
  );
};

export default SavingsProgram;
